import type { DgaConfig } from './dga.js'

/**
 * Frozen production parameters of the Vietnam anti-blocking domain sequence.
 * Imported identically by the API server and the client PWA — this is the single
 * source of truth. Bumping `version` forks the entire sequence, so treat changes
 * as a coordinated client+server rollout.
 *
 * `.vn` is deliberately absent: a `.vn` domain is taken down with one registry
 * order, whereas a global `.com` is only reachable to block via DNS — which the
 * rotation + signed-beacon design defeats.
 *
 * `seed`/`version` are NOT secrets — they ship in the client bundle, so a censor can
 * derive the whole sequence too. Secrecy is not the defense and never was: the signed
 * beacon defeats *trusting* a hostile IP, and availability rests on our registration
 * cadence (register + light up the next domain faster than the censor blocks the
 * current one), not on the sequence being unpredictable.
 */
export const VN_DGA_CONFIG: DgaConfig = {
  labelLength: 16,
  seed: 'pt5598-vn-access-v1',
  tlds: ['com'],
  version: 1,
}

/**
 * Pinned ed25519 public key (base64url, 32-byte) of the vn-access beacon trust-gate — the client's
 * single trust anchor. The client accepts a beacon ONLY if its signature verifies against THIS key;
 * a resolved domain or a live IP is never trusted on its own. The private counterpart lives solely
 * in production SSM (`/production/tsar/vn-access/VN_ACCESS_SIGNING_KEY`, SecureString) and is held
 * only by the live origin.
 *
 * This is public, not a secret — pinning it in the bundle is the point. Rotating the key means
 * regenerating the pair, updating the SSM SecureString, and bumping this constant in a coordinated
 * client+server release (older signed beacons stop verifying).
 */
export const VN_ACCESS_BEACON_PUBLIC_KEY = 'um5HvCYt3U66yoxCX0Z7pUJ-daxK2hWACOyAivBndIQ'
